/**
 * FeedbackPage
 * Kitchly v0.5.4
 *
 * In-app feedback form for alpha testers.
 * Types: bug report, feature suggestion, general comment.
 * Saves to DB + sends email notification to info@kitchly.ca.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Bug,
  Lightbulb,
  MessageCircle,
  Send,
  CheckCircle
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function FeedbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Detect which page the user came from
  const fromPage = location.state?.from || '';

  const typeOptions = [
    { value: 'bug', icon: Bug, label: t('feedback.typeBug'), color: '#D9534F', bg: '#FDECEA' },
    { value: 'feature', icon: Lightbulb, label: t('feedback.typeFeature'), color: '#C4956A', bg: '#FFF3E0' },
    { value: 'comment', icon: MessageCircle, label: t('feedback.typeComment'), color: '#7EB8D4', bg: '#E3F2FD' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || message.trim().length < 5) {
      toast.error(t('feedback.tooShort'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('kitchly_access_token');
      const res = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type,
          message: message.trim(),
          page: fromPage || undefined
        })
      });

      if (!res.ok) throw new Error('Submit failed');

      setSubmitted(true);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleNewFeedback = () => {
    setSubmitted(false);
    setType('bug');
    setMessage('');
  };

  return (
    <div className="feedback-page" style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-md)', gap: 'var(--space-md)' }}>
        <button className="btn-icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ flex: 1, margin: 0 }}>{t('feedback.title')}</h1>
      </div>

      <div style={{ padding: '0 var(--space-md)' }}>

        {/* === SUCCESS STATE === */}
        {submitted ? (
          <div className="card" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
            <CheckCircle size={56} color="var(--color-primary)" style={{ marginBottom: 'var(--space-md)' }} />
            <h2 style={{ margin: '0 0 var(--space-sm)', fontFamily: 'var(--font-heading)' }}>
              {t('feedback.thankYou')}
            </h2>
            <p className="text-secondary" style={{ marginBottom: 'var(--space-lg)' }}>
              {t('feedback.thankYouMessage')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <button className="btn btn-secondary btn-full" onClick={handleNewFeedback}>
                {t('feedback.submitAnother')}
              </button>
              <button className="btn btn-ghost btn-full" onClick={() => navigate('/settings')}>
                {t('feedback.backToSettings')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Intro */}
            <div className="banner banner-info mb-md" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
              <MessageCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{t('feedback.intro')}</span>
            </div>

            <form onSubmit={handleSubmit}>

              {/* Feedback Type Selector */}
              <div className="form-group">
                <label className="form-label">{t('feedback.typeLabel')}</label>
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  {typeOptions.map(opt => {
                    const Icon = opt.icon;
                    const isSelected = type === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setType(opt.value)}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 6,
                          padding: 'var(--space-sm) var(--space-xs)',
                          borderRadius: 'var(--radius-md)',
                          border: isSelected ? `2px solid ${opt.color}` : '2px solid var(--color-border)',
                          background: isSelected ? opt.bg : 'var(--color-bg-card)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Icon size={22} color={isSelected ? opt.color : 'var(--color-text-light)'} />
                        <span style={{
                          fontSize: '12px',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? opt.color : 'var(--color-text-secondary)'
                        }}>
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div className="form-group">
                <label className="form-label" htmlFor="feedback-message">
                  {type === 'bug' ? t('feedback.messageBug') :
                   type === 'feature' ? t('feedback.messageFeature') :
                   t('feedback.messageComment')}
                </label>
                <textarea
                  id="feedback-message"
                  className="form-input"
                  placeholder={
                    type === 'bug' ? t('feedback.placeholderBug') :
                    type === 'feature' ? t('feedback.placeholderFeature') :
                    t('feedback.placeholderComment')
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  required
                  minLength={5}
                  maxLength={5000}
                  style={{ resize: 'vertical' }}
                />
                <p className="text-xs text-secondary mt-sm" style={{ textAlign: 'right' }}>
                  {message.length}/5000
                </p>
              </div>

              {/* Current page info */}
              {fromPage && (
                <p className="text-xs text-secondary mb-md">
                  {t('feedback.pageContext', { page: fromPage })}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="btn btn-primary btn-full btn-lg"
                disabled={loading || message.trim().length < 5}
              >
                <Send size={18} />
                {loading ? t('common.loading') : t('feedback.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default FeedbackPage;
